'use client';

import { useCallback } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { viewPreferencesFetch } from '@/lib/view-preferences/api';
import { viewPreferenceKeys } from '@/hooks/api/query-keys';
import type {
  ViewPreferenceState,
  ViewPreferenceGetResponse,
  ViewPreferencePutResponse,
} from '@/lib/types/view-preferences';

/**
 * Per-user, per-workspace list-view state hook.
 *
 * Round trip:
 *   on mount  → GET   /v1/users/me/view-preferences/<viewKey>
 *   on change → PUT   /v1/users/me/view-preferences/<viewKey>  (fire-and-forget)
 *   on clear  → DELETE /v1/users/me/view-preferences/<viewKey>
 *
 * localStorage is a side-cache, not the source of truth — it seeds
 * `placeholderData` so the first render uses the user's last-known state
 * without a network round-trip, then reconciles when the BE response
 * arrives. Mutations write through localStorage AND post to the BE so a
 * subsequent tab opening on the same device feels instant.
 *
 * Super-admin impersonation: the BE silently no-ops PUT/DELETE (returns
 * `persisted: false`). The optimistic update still lands locally so the
 * impersonation tab feels responsive — the customer's saved view is
 * never mutated.
 */
export function useViewPreferences<T extends ViewPreferenceState = ViewPreferenceState>(
  viewKey: string,
) {
  const queryClient = useQueryClient();
  const queryKey = viewPreferenceKeys.byView(viewKey);

  const localKey = lsKey('production', viewKey);

  // TanStack v5 PlaceholderDataFunction uses NonFunctionGuard<T> which the
  // compiler can't prove generic T satisfies — the runtime shape is always
  // a plain object envelope. Cast through `unknown` keeps the call typed
  // for consumers while accepting the placeholder shape locally.
  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<T> => {
      const res = await viewPreferencesFetch<ViewPreferenceGetResponse<T>>(
        viewKey,
      );
      writeLocalCache<T>(localKey, res.state);
      return res.state;
    },
    placeholderData: ((): T | undefined =>
      readLocalCache<T>(localKey)) as unknown as undefined,
    staleTime: 5 * 60 * 1000,
  });

  const setMutation = useMutation({
    mutationFn: (next: T) =>
      viewPreferencesFetch<ViewPreferencePutResponse<T>>(viewKey, {
        method: 'PUT',
        body: JSON.stringify({ state: next }),
      }),
    onMutate: (next: T) => {
      // Optimistic — cache + localStorage update synchronously so the
      // calling component sees the new state on its next render.
      queryClient.setQueryData<T>(queryKey, next);
      writeLocalCache(localKey, next);
    },
    // No onError revert — BE state will reconcile on the next refetch.
    // A failed PUT is rare; when it happens the user's local cache
    // diverges briefly until the next page load.
  });

  const clearMutation = useMutation({
    mutationFn: () =>
      viewPreferencesFetch<void>(viewKey, { method: 'DELETE' }),
    onMutate: () => {
      queryClient.setQueryData<T>(queryKey, {} as T);
      clearLocalCache(localKey);
    },
  });

  const setState = useCallback(
    (next: T) => setMutation.mutate(next),
    [setMutation],
  );
  const setStateAsync = useCallback(
    (next: T) => setMutation.mutateAsync(next),
    [setMutation],
  );
  const clearAll = useCallback(
    () => clearMutation.mutate(),
    [clearMutation],
  );
  const clearAllAsync = useCallback(
    () => clearMutation.mutateAsync(),
    [clearMutation],
  );

  return {
    state: (query.data ?? ({} as T)) as T,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    setState,
    setStateAsync,
    clearAll,
    clearAllAsync,
  };
}

// ── localStorage side-cache ────────────────────────────────────────────────

const LS_PREFIX = 'hf-app:view-pref';

function lsKey(mode: string, viewKey: string): string {
  return `${LS_PREFIX}:${mode}:${viewKey}`;
}

function readLocalCache<T>(key: string): T | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}

function writeLocalCache<T>(key: string, state: T): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Quota-exceeded or private-mode — swallow. BE remains source of truth.
  }
}

function clearLocalCache(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}
